import { useRef, type ClipboardEvent, type KeyboardEvent, type ChangeEvent } from 'react';
import { clsx } from '../clsx';
import { useFieldIds } from '../hooks';
import styles from './CodeField.module.css';

export interface CodeFieldProps {
  /** Accessible name of the group ("Bestätigungscode"). */
  label: string;
  labelHidden?: boolean | undefined;
  length?: number | undefined;
  /** Digits entered so far (0 to `length` characters). */
  value: string;
  onChange: (value: string) => void;
  /** Called once all boxes are filled. */
  onComplete?: ((value: string) => void) | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  invalid?: boolean | undefined;
  disabled?: boolean | undefined;
  autoFocus?: boolean | undefined;
  name?: string | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

/** The six 2FA boxes of Shell 17: 52 px, display font 22 px, auto-advance, paste distributes the digits. */
export function CodeField({
  label,
  labelHidden = false,
  length = 6,
  value,
  onChange,
  onComplete,
  hint,
  error,
  invalid,
  disabled,
  autoFocus,
  name,
  id,
  className,
}: CodeFieldProps) {
  const ids = useFieldIds(id, { hint: Boolean(hint), error: Boolean(error) });
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, index) => value.charAt(index));

  function focusBox(index: number) {
    const target = inputs.current[Math.max(0, Math.min(length - 1, index))];
    target?.focus();
    target?.select();
  }

  function update(next: string) {
    const clean = next.replace(/\D/g, '').slice(0, length);
    onChange(clean);
    if (clean.length === length && clean !== value) onComplete?.(clean);
  }

  function handleChange(index: number, event: ChangeEvent<HTMLInputElement>) {
    const typed = event.target.value.replace(/\D/g, '');
    if (!typed) {
      update(value.slice(0, index) + value.slice(index + 1));
      return;
    }
    const digit = typed.charAt(typed.length - 1);
    const at = Math.min(index, value.length);
    const next = value.slice(0, at) + digit + value.slice(at + 1);
    update(next);
    focusBox(Math.min(length - 1, at < value.length ? at + 1 : next.length));
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        update(value.slice(0, index) + value.slice(index + 1));
      } else if (index > 0) {
        update(value.slice(0, index - 1) + value.slice(index));
        focusBox(index - 1);
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
    }
  }

  function handlePaste(index: number, event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    event.preventDefault();
    const next = (value.slice(0, index) + pasted).slice(0, length);
    update(next);
    focusBox(Math.min(length - 1, next.length));
  }

  return (
    <div
      role="group"
      aria-labelledby={`${ids.inputId}-label`}
      aria-describedby={ids.describedBy}
      className={clsx(styles.wrapper, disabled && styles.disabled, className)}
    >
      <span id={`${ids.inputId}-label`} className={clsx(styles.label, labelHidden && styles.srOnly)}>
        {label}
      </span>
      <div className={styles.boxes} style={{ gridTemplateColumns: `repeat(${length}, 1fr)` }}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputs.current[index] = element;
            }}
            id={index === 0 ? ids.inputId : `${ids.inputId}-${index}`}
            name={index === 0 ? name : undefined}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={length}
            aria-label={`Ziffer ${index + 1} von ${length}`}
            aria-invalid={error || invalid ? true : undefined}
            autoFocus={autoFocus && index === 0}
            disabled={disabled}
            value={digit}
            className={clsx(styles.box, (error || invalid) && styles.invalid)}
            onChange={(event) => handleChange(index, event)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={(event) => handlePaste(index, event)}
            onFocus={(event) => event.target.select()}
          />
        ))}
      </div>
      {error ? (
        <div id={ids.errorId} className={styles.error} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div id={ids.hintId} className={styles.hint}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}
